// artifacts/edison/src/pages/integrations/devices-tab.tsx
import { useState, useEffect } from 'react';
import { Monitor, Plus, Trash2, CheckCircle, AlertTriangle, Copy, Check, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { axiosInstance } from '@edison/api-client-react';

type Device = {
  id: string;
  label: string;
  lastSeenAt: string | null;
  scriptHash: string | null;
  hookCurrent: boolean;
  revokedAt: string | null;
  createdAt: string;
};

type RegisterStep = 'disclosure' | 'label' | 'instructions';

function UpdateScripts() {
  const [scripts, setScripts] = useState<HookScripts | null>(null);
  const [scriptsError, setScriptsError] = useState(false);

  useEffect(() => {
    axiosInstance.get<{ data: HookScripts }>('/api/machine-registrations/hook-scripts')
      .then((res) => setScripts(res.data?.data ?? null))
      .catch(() => setScriptsError(true));
  }, []);

  if (scriptsError) {
    return <p className="text-xs text-destructive">Failed to load scripts — refresh and try again.</p>;
  }

  return (
    <div className="space-y-4 mt-3 border-t border-amber-200 pt-3">
      <p className="text-xs text-amber-800">
        Replace the scripts on each outdated device. Your machine key and config don&apos;t change.
      </p>
      <div className="space-y-1">
        <label className="text-xs font-medium block">
          Heartbeat script
          {scripts && <span className="ml-2 font-normal text-amber-700">({scripts.version})</span>}
        </label>
        {scripts ? (
          <pre className="rounded-md bg-amber-100 px-3 py-2 text-xs font-mono whitespace-pre-wrap overflow-x-auto border border-amber-200 max-h-40 overflow-y-auto">
            {scripts.heartbeat}
          </pre>
        ) : (
          <div className="h-16 rounded-md bg-amber-100 animate-pulse" />
        )}
        <p className="text-xs text-amber-700">
          Save as <code className="bg-amber-100 px-1 rounded">~/.local/bin/edison-heartbeat</code> then <code className="bg-amber-100 px-1 rounded">chmod +x</code> it.
        </p>
      </div>
      <div className="space-y-1">
        <label className="text-xs font-medium block">Session-end script</label>
        {scripts ? (
          <pre className="rounded-md bg-amber-100 px-3 py-2 text-xs font-mono whitespace-pre-wrap overflow-x-auto border border-amber-200 max-h-40 overflow-y-auto">
            {scripts.sessionEnd}
          </pre>
        ) : (
          <div className="h-16 rounded-md bg-amber-100 animate-pulse" />
        )}
        <p className="text-xs text-amber-700">
          Save as <code className="bg-amber-100 px-1 rounded">~/.local/bin/edison-session-end</code> then <code className="bg-amber-100 px-1 rounded">chmod +x</code> it.
        </p>
      </div>
    </div>
  );
}

type HookScripts = { version: string; heartbeat: string; sessionEnd: string };

function SetupInstructions({ deviceKey, label: _label }: { deviceKey: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const [scripts, setScripts] = useState<HookScripts | null>(null);
  const [scriptsError, setScriptsError] = useState(false);

  useEffect(() => {
    axiosInstance.get<{ data: HookScripts }>('/api/machine-registrations/hook-scripts')
      .then((res) => setScripts(res.data?.data ?? null))
      .catch(() => setScriptsError(true));
  }, []);

  const hookSnippet = `{
  "hooks": {
    "PostToolUse": [{
      "hooks": [{"type": "command", "command": "~/.local/bin/edison-heartbeat"}]
    }],
    "Stop": [{
      "hooks": [{"type": "command", "command": "~/.local/bin/edison-session-end"}]
    }]
  }
}`;

  const copyKey = () => {
    navigator.clipboard.writeText(deviceKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-green-200 bg-green-50 p-4 flex items-start gap-2 text-sm text-green-800">
        <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
        <span>Device registered. Copy your key now — it won&apos;t be shown again.</span>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium block">Your machine key</label>
        <div className="flex gap-2">
          <code className="flex-1 rounded-md bg-muted px-3 py-2 text-sm font-mono break-all border">
            {deviceKey}
          </code>
          <Button variant="secondary" size="sm" onClick={copyKey} aria-label="Copy machine key">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium block">1. Create config file</label>
        <pre className="rounded-md bg-muted px-3 py-2 text-xs font-mono whitespace-pre-wrap border">
          {`mkdir -p ~/.config/edison\nprintf 'EDISON_KEY=${deviceKey}\\n' > ~/.config/edison/config`}
        </pre>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium block">
          2. Save heartbeat script
          {scripts && <span className="ml-2 font-normal text-muted-foreground">({scripts.version})</span>}
        </label>
        {scriptsError ? (
          <p className="text-xs text-destructive">Failed to load scripts — refresh and try again.</p>
        ) : scripts ? (
          <pre className="rounded-md bg-muted px-3 py-2 text-xs font-mono whitespace-pre-wrap overflow-x-auto border max-h-48 overflow-y-auto">
            {scripts.heartbeat}
          </pre>
        ) : (
          <div className="h-20 rounded-md bg-muted animate-pulse" />
        )}
        <p className="text-xs text-muted-foreground">
          Save as <code>~/.local/bin/edison-heartbeat</code> then run{' '}
          <code>chmod +x ~/.local/bin/edison-heartbeat</code>.
        </p>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium block">3. Save session-end script</label>
        {scriptsError ? (
          <p className="text-xs text-destructive">Failed to load scripts — refresh and try again.</p>
        ) : scripts ? (
          <pre className="rounded-md bg-muted px-3 py-2 text-xs font-mono whitespace-pre-wrap overflow-x-auto border max-h-48 overflow-y-auto">
            {scripts.sessionEnd}
          </pre>
        ) : (
          <div className="h-20 rounded-md bg-muted animate-pulse" />
        )}
        <p className="text-xs text-muted-foreground">
          Save as <code>~/.local/bin/edison-session-end</code> then run{' '}
          <code>chmod +x ~/.local/bin/edison-session-end</code>.
        </p>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium block">4. Add hooks to ~/.claude/settings.json</label>
        <pre className="rounded-md bg-muted px-3 py-2 text-xs font-mono whitespace-pre-wrap overflow-x-auto border">
          {hookSnippet}
        </pre>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium block">5. Verify (optional)</label>
        <pre className="rounded-md bg-muted px-3 py-2 text-xs font-mono border">
          {`curl -sf -X POST https://edison.polusailabs.com/api/cc-events \\\n  -H 'X-Edison-Key: ${deviceKey}' \\\n  -H 'Content-Type: application/json' \\\n  -d '{"ts":"2024-01-01T00:00:00Z","tool":"test","project":"my-project","scriptHash":"v3"}'`}
        </pre>
      </div>
    </div>
  );
}

function DisclosurePanel({ onAcknowledge }: { onAcknowledge: () => void }) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border p-4 space-y-3 text-sm">
        <p className="font-semibold">What Edison captures from your Claude Code sessions:</p>
        <ul className="list-disc list-inside space-y-1 text-muted-foreground">
          <li>Which files you edited or read (relative paths only — your folder structure is never sent)</li>
          <li>When you were working (session start and end time)</li>
          <li>Your conversation with Claude (full transcript, stored encrypted, accessible only to your team)</li>
        </ul>
        <p className="font-semibold mt-3">What Edison does NOT capture:</p>
        <ul className="list-disc list-inside space-y-1 text-muted-foreground">
          <li>Your local folder path or machine username</li>
          <li>Shell commands you ran</li>
          <li>Anything outside your Claude Code sessions</li>
        </ul>
        <p className="font-semibold mt-3">How it&apos;s used:</p>
        <ul className="list-disc list-inside space-y-1 text-muted-foreground">
          <li>To create time entries for R&amp;D timewriting review</li>
          <li>To provide evidence of technical investigation for AusIndustry purposes</li>
          <li>You can revoke your machine key from Edison at any time</li>
        </ul>
      </div>
      <Button onClick={onAcknowledge} className="w-full">I understand — continue</Button>
    </div>
  );
}

export function DevicesTab() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [registerStep, setRegisterStep] = useState<RegisterStep | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [newKey, setNewKey] = useState<string | null>(null);
  const [registering, setRegistering] = useState(false);
  const [showUpdateScripts, setShowUpdateScripts] = useState(false);

  useEffect(() => {
    axiosInstance.get<{ data: { items: Device[] } }>('/api/machine-registrations')
      .then((res) => {
        const items = res.data?.data?.items ?? [];
        setDevices(items);
        if (items.length > 0) {
          localStorage.setItem('edison:setup-device', 'true');
        }
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load devices');
        setLoading(false);
      });
  }, []);

  const handleRegister = async () => {
    if (!newLabel.trim()) return;
    setRegistering(true);
    try {
      const res = await axiosInstance.post<{ data: ({ key: string } & Device) }>('/api/machine-registrations', { label: newLabel.trim() });
      if (res.data?.data?.key) {
        setNewKey(res.data.data.key);
        setRegisterStep('instructions');
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleRevoke = async (deviceId: string) => {
    await axiosInstance.delete(`/api/machine-registrations/${deviceId}`);
    setDevices((prev) => prev.filter((d) => d.id !== deviceId));
  };

  if (loading) {
    return <div className="h-40 rounded-lg bg-muted animate-pulse" />;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center gap-2 text-destructive text-sm">
        <span>{error}</span>
      </div>
    );
  }

  if (registerStep === 'disclosure') {
    return (
      <div className="max-w-lg space-y-4">
        <h3 className="font-semibold">Before you continue</h3>
        <DisclosurePanel onAcknowledge={() => setRegisterStep('label')} />
      </div>
    );
  }

  if (registerStep === 'label') {
    return (
      <div className="max-w-sm space-y-4">
        <h3 className="font-semibold">Name your device</h3>
        <div className="space-y-2">
          <label className="text-xs font-medium block" htmlFor="device-label">Device label</label>
          <input
            id="device-label"
            className="w-full rounded border bg-background px-3 py-2 text-sm"
            placeholder="e.g. MacBook Pro — work"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={() => void handleRegister()} disabled={!newLabel.trim() || registering}>
            {registering ? 'Registering…' : 'Register device'}
          </Button>
          <Button variant="secondary" onClick={() => setRegisterStep(null)}>Cancel</Button>
        </div>
      </div>
    );
  }

  if (registerStep === 'instructions' && newKey) {
    return (
      <div className="max-w-2xl space-y-4">
        <h3 className="font-semibold">Setup instructions</h3>
        <SetupInstructions deviceKey={newKey} label={newLabel} />
        <Button onClick={() => { setRegisterStep(null); setNewKey(null); setNewLabel(''); }}>
          Done
        </Button>
      </div>
    );
  }

  const activeDevices = devices.filter((d) => !d.revokedAt);
  const revokedDevices = devices.filter((d) => d.revokedAt);
  const outdatedDevices = activeDevices.filter((d) => !d.hookCurrent);

  return (
    <div className="space-y-6">
      {outdatedDevices.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
            <div className="flex-1">
              <p className="font-medium">
                {outdatedDevices.length === 1
                  ? `${outdatedDevices[0]!.label} has an outdated Edison hook.`
                  : `${outdatedDevices.length} devices have outdated Edison hooks.`}
              </p>
              <p className="text-amber-800 mt-0.5">
                Re-copy the scripts below onto each affected device to update.
              </p>
            </div>
            <button
              onClick={() => setShowUpdateScripts((v) => !v)}
              className="shrink-0 text-xs font-medium text-amber-700 underline underline-offset-2 hover:text-amber-900"
            >
              {showUpdateScripts ? 'Hide scripts' : 'Show update scripts'}
            </button>
          </div>
          {showUpdateScripts && <UpdateScripts />}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm">Registered devices</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Claude Code machines authorised to send activity to Edison.
          </p>
        </div>
        <Button onClick={() => setRegisterStep('disclosure')} size="sm">
          <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
          Register this machine
        </Button>
      </div>

      {activeDevices.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <Monitor className="mx-auto h-8 w-8 text-muted-foreground mb-3" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">No devices registered yet.</p>
          <p className="text-xs text-muted-foreground mt-1">
            Register this machine to start capturing Claude Code sessions as R&amp;D evidence.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {activeDevices.map((device) => (
            <div key={device.id} className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Monitor className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  <span className="font-medium text-sm">{device.label}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>
                    Last seen: {device.lastSeenAt
                      ? new Date(device.lastSeenAt).toLocaleDateString()
                      : 'Never'}
                  </span>
                  {device.scriptHash ? (
                    device.hookCurrent ? (
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="h-3 w-3" aria-hidden="true" />
                        Hook {device.scriptHash} — current
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-amber-600">
                        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                        Hook {device.scriptHash} — outdated
                      </span>
                    )
                  ) : (
                    <span className="flex items-center gap-1 text-amber-600">
                      <RefreshCw className="h-3 w-3" aria-hidden="true" />
                      Hook version unknown — re-copy scripts above
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => void handleRevoke(device.id)}
                aria-label={`Revoke ${device.label}`}
                className="text-muted-foreground hover:text-destructive transition-colors p-1"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}

      {revokedDevices.length > 0 && (
        <details className="text-sm">
          <summary className="text-muted-foreground cursor-pointer">
            {revokedDevices.length} revoked device{revokedDevices.length > 1 ? 's' : ''}
          </summary>
          <div className="mt-2 space-y-1">
            {revokedDevices.map((d) => (
              <div key={d.id} className="flex items-center gap-2 text-muted-foreground px-1">
                <Trash2 className="h-3 w-3" aria-hidden="true" />
                <span>{d.label}</span>
                <span className="text-xs">
                  revoked {new Date(d.revokedAt!).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
